import { ComboBox, Tooltip } from '@airbus/components-react';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DropdownOption } from '../../../shared/types';
import { useGridDataContext } from '../../../shared/context/GridDataContext';
import { useLanguageContextAPI } from '../../../shared/context/LanguageContext';
import { isSameDate } from '../../../shared/utils/dateUtils';
import { getAllDropdowns } from '../../../shared/services/allDropdownsRegistry';
import { focusField } from '../../../shared/components/grid/RenderEditField';
import styles from './ErledigtAvEditCell.module.css';
import {
  applyErledigtAvYesGuard,
  getErledigtAvDisabledTooltipKey,
  isErledigtAvYesEnabled,
} from '../services/erledigtAvNeedGate';

export default function ErledigtAvEditCell(props: any) {
  const { t } = useLanguageContextAPI();
  const { id, field, colDef, row, value: initialValue } = props;
  const apiRef = useGridApiContext();
  const [, setRows] = useGridDataContext();
  const allDropdowns = getAllDropdowns();

  const baseOptions = useMemo(
    () => (colDef?.valueOptions ?? []).filter((item: DropdownOption) => item?.value != null),
    [colDef?.valueOptions],
  );
  const valueOptions = useMemo(
    () => applyErledigtAvYesGuard({ options: baseOptions, row, allDropdowns }),
    [baseOptions, row, allDropdowns],
  );

  const canFinalize = isErledigtAvYesEnabled({ row, allDropdowns });
  const disabledTooltip = t(getErledigtAvDisabledTooltipKey({ row, allDropdowns }));

  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null);
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    const initial = valueOptions.find((item: DropdownOption) => item.value == initialValue) ?? null;
    setSelectedOption(initial);
  }, [initialValue, valueOptions]);

  useEffect(() => {
    if (hasOpenedRef.current || valueOptions.length === 0) {
      return;
    }

    hasOpenedRef.current = true;
    focusField({ id, field });
  }, [id, field, valueOptions.length]);

  const handleValueChange = (event: DropdownOption | null) => {
    const valueObj = event ?? null;
    const newValue = valueObj?.value ?? null;

    if (valueObj?.disabled) {
      return;
    }

    if (colDef?.onValueChange) {
      const valid = colDef.onValueChange({ rowId: id, value: newValue, row });
      if (valid === false) {
        apiRef.current.setEditCellValue({ id, field, value: initialValue });
        apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });
        return;
      }
    }

    setSelectedOption(valueObj);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  const blurHandler = (targetValue: unknown) => {
    apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });

    setRows((prestate: any[]) => {
      const oldRow = prestate.find((item) => item.id === row.id);
      if (!oldRow) {
        return prestate;
      }

      if (isSameDate(oldRow[field], targetValue)) {
        return prestate;
      }

      if (oldRow[field] === targetValue) {
        return prestate;
      }

      oldRow[field] = targetValue;
      if (oldRow.status === 'N' || oldRow.status === 'C') {
        oldRow.touched[field] = true;
      }
      oldRow.status = oldRow.status || 'E';

      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id === row.id);
      newState.splice(index, 1, oldRow);
      return newState;
    });
  };

  const handleEnterTabBlur = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      const target = e.target as HTMLInputElement;
      target.blur();
    }
  };

  const comboBox = (
    <ComboBox
      className='h-100'
      id={id + '_' + field}
      hideClear={true}
      options={valueOptions}
      value={selectedOption}
      getOptionLabel={(option: DropdownOption) => option.label}
      getOptionValue={(option: DropdownOption) => option.value}
      onChange={handleValueChange}
      onBlur={() => blurHandler(selectedOption?.value ?? null)}
      onKeyDown={handleEnterTabBlur}
    />
  );
  const wrapperStyle = {
    width: colDef?.computedWidth ?? colDef?.width ?? '100%',
  };

  const wrappedComboBox = (
    <div className={styles.container} style={wrapperStyle}>
      {comboBox}
    </div>
  );

  return !canFinalize ? (
    <Tooltip label={disabledTooltip} placement='top'>
      {wrappedComboBox}
    </Tooltip>
  ) : (
    wrappedComboBox
  );
}